import { FC } from 'react'

import * as RadixSwitch from '@radix-ui/react-switch'
import { styled } from 'styled-components'

import { onSurfaceVariant, outline, success, onSurfaceDisabled } from 'core/styles'

export type SwitchProps = {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  id?: string
  'aria-label'?: string
  label?: string
  labelPosition?: 'left' | 'right'
}

const Switch: FC<SwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  id,
  'aria-label': ariaLabel,
  label,
  labelPosition = 'right',
}) => {
  const switchElement = (
    <StyledSwitchRoot
      $disabled={disabled}
      aria-label={ariaLabel}
      checked={checked}
      disabled={disabled}
      id={id}
      onCheckedChange={onChange}
    >
      <StyledSwitchThumb />
    </StyledSwitchRoot>
  )

  const labelElement = (
    <SwitchLabel $checked={checked} $disabled={disabled}>
      {label ?? (checked ? 'True' : 'False')}
    </SwitchLabel>
  )

  return (
    <SwitchContainer $labelAlign={labelPosition}>
      {switchElement}
      {labelElement}
    </SwitchContainer>
  )
}

export default Switch

const SwitchContainer = styled.div<{ $labelAlign?: 'left' | 'right' }>`
  display: inline-flex;
  flex-direction: ${({ $labelAlign }) => ($labelAlign === 'left' ? 'row-reverse' : 'row')};
  gap: 8px;
  align-items: center;
`

const SwitchLabel = styled.span<{ $checked: boolean; $disabled: boolean }>`
  min-width: 40px;
  height: 20px;
  text-align: center;
  color: ${({ $disabled }) => ($disabled ? onSurfaceVariant : 'inherit')};
  font-size: 14px;
  font-weight: 600;
`

const StyledSwitchRoot = styled(RadixSwitch.Root)<{ $disabled?: boolean }>`
  all: unset;
  position: relative;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  border-radius: 16px;
  box-shadow: 0 2px 10px ${outline};
  background-color: ${onSurfaceDisabled};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  width: 44px;
  height: 20px;
  -webkit-tap-highlight-color: rgb(0 0 0 / 0%);

  &[data-state='checked'] {
    background-color: ${success};
  }
`

const StyledSwitchThumb = styled(RadixSwitch.Thumb)`
  display: block;
  transform: translateX(2px);
  transition: transform 100ms;
  border-radius: 32px;
  background-color: white;
  width: 20px;
  height: 16px;
  will-change: transform;

  &[data-state='checked'] {
    transform: translateX(22px);
  }
`
